/**
 * PageHeader - Consistent navigation header for company pages.
 * Includes back button, title, and optional action.
 */

import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Home } from 'lucide-react';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  backTo?: string;
  backLabel?: string;
  showHome?: boolean;
  action?: React.ReactNode;
}

export default function PageHeader({
  title,
  subtitle,
  backTo,
  backLabel,
  showHome = true,
  action,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');

  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  };

  const isRtl = i18n.dir() === 'rtl';

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10" dir={i18n.dir()}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          {/* Start section: back button + optional home.
              In RTL we keep ArrowLeft as the same component but rotate it 180°
              so the arrowhead points toward the edge of the page (i.e., away
              from the center title) — that matches the reader's "go back"
              direction in RTL flow. */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={handleBack}
              aria-label={backLabel || t('back', 'Back')}
              className="inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <ArrowLeft className={`w-5 h-5 flex-shrink-0 ${isRtl ? 'rotate-180' : ''}`} aria-hidden="true" />
              <span className="hidden sm:inline truncate max-w-[140px]">
                {backLabel || t('back', 'Back')}
              </span>
            </button>

            {showHome && (
              <Link
                to="/"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                title={t('home', 'Home')}
                aria-label={t('home', 'Home')}
              >
                <Home className="w-5 h-5" aria-hidden="true" />
              </Link>
            )}
          </div>

          {/* Center title */}
          <div className="flex-1 min-w-0 text-center">
            <h1 className="text-lg font-semibold text-[var(--pv-color-primary)] truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-500 truncate">{subtitle}</p>
            )}
          </div>

          {/* End section: action */}
          <div className="flex items-center gap-2 min-w-0 justify-end">
            {action}
          </div>
        </div>
      </div>
    </div>
  );
}
